import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiService } from '../services/apiService'
import posthog from '../posthog'

interface User {
  email: string
  isAuthenticated: boolean
  id?: number
  companyName?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  login: (email: string) => Promise<boolean>
  logout: () => void
  clearError: () => void
  validateToken: () => Promise<{ isValid: boolean; userData?: any }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Validate token with backend and get user info
  const validateToken = async (): Promise<{ isValid: boolean; userData?: any }> => {
    const token = localStorage.getItem('yyz-cbp-token')
    const email = localStorage.getItem('yyz-cbp-email')
    
    if (!token || !email) {
      return { isValid: false }
    }

    try {
      // Import apiService dynamically to avoid circular dependency
      const { apiService } = await import('../services/apiService')
      
      // Use the API service which includes proper obfuscation headers
      const response = await apiService.getClientBusinessInfo()
      
      return { 
        isValid: true, 
        userData: { 
          email, 
          isAuthenticated: true,
          companyName: response.data?.company_name 
        }
      }
    } catch (error: any) {
      // Check if it's an auth failure (401) or other error
      if (error.message?.includes('session has expired') || error.message?.includes('401')) {
        return { isValid: false }
      }
      
      // For network errors or non-auth issues, assume token is still valid
      return { 
        isValid: true, 
        userData: { 
          email, 
          isAuthenticated: true 
        }
      }
    }
  }

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true)
      setError(null)

      try {
        const token = localStorage.getItem('yyz-cbp-token')
        const email = localStorage.getItem('yyz-cbp-email')



        if (token && email) {
          // First, optimistically set user state with cached data
          setUser({ 
            email, 
            isAuthenticated: true 
          })

          // Then validate token with backend in the background
          setTimeout(async () => {
            try {
              const validation = await validateToken()
              
              if (validation.isValid && validation.userData) {
                setUser(validation.userData)
                posthog.identify(email, {
                  email: email,
                  companyName: validation.userData.companyName
                })
              } else if (!validation.isValid) {
                // Only clear if validation explicitly failed (401)
                localStorage.removeItem('yyz-cbp-token')
                localStorage.removeItem('yyz-cbp-email')
                setUser(null)
              }
            } catch (validationError) {
              // Keep the optimistic state if validation had network issues
            }
          }, 500) // Validate after a short delay to avoid race conditions
        } else {
          setUser(null)
        }
      } catch (err) {
        // Only clear auth data if we have a serious error
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email: string): Promise<boolean> => {
    try {
      setError(null)
      setLoading(true)
      
      const response = await apiService.login(email)
      
      if (response.success && response.token) {
        const authenticatedEmail = response.user?.email || email

        // Store auth info
        localStorage.setItem('yyz-cbp-email', authenticatedEmail)
        localStorage.setItem('yyz-cbp-token', response.token)
        
        const userData = { 
          email: authenticatedEmail, 
          isAuthenticated: true,
          id: response.user?.id,
          companyName: response.user?.companyName
        }
        
        setUser(userData)
        
        // Identify user in PostHog
        posthog.identify(email, {
          email: email,
          companyName: response.user?.companyName
        })
        
        // Track login event
        posthog.capture('user_logged_in', {
          email: email,
          companyName: response.user?.companyName
        })
        
        
        return true
      } else {
        setError(response.error || 'Login failed. Please check your email and try again.')
        return false
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your connection and try again.')
      return false
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    // Track logout event
    posthog.capture('user_logged_out')
    
    localStorage.removeItem('yyz-cbp-token')
    localStorage.removeItem('yyz-cbp-email')
    setUser(null)
    setError(null)
    
    // Reset PostHog user
    posthog.reset()
  }

  const clearError = () => {
    setError(null)
  }

  const contextValue: AuthContextType = {
    user,
    loading,
    error,
    login,
    logout,
    clearError,
    validateToken
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
